import type { SshProfile } from '../types/ssh';

const DEFAULT_PORT = 22;

export function normalizeSshProfiles(value: unknown): SshProfile[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => normalizeSshProfile(entry))
    .filter((profile): profile is SshProfile => Boolean(profile))
    .sort((left, right) => left.name.localeCompare(right.name));
}

export function normalizeSshProfile(value: unknown): SshProfile | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const profile = value as Partial<SshProfile>;
  const host = String(profile.host || '').trim();
  const name = String(profile.name || '').trim();
  const username = String(profile.username || '').trim();

  if (!host || !name || !username) {
    return null;
  }

  const now = new Date().toISOString();
  const authType = profile.authType === 'privateKey' ? 'privateKey' : 'password';
  const port = Number.isFinite(Number(profile.port)) ? Number(profile.port) : DEFAULT_PORT;

  return {
    id: String(profile.id || crypto.randomUUID()),
    name,
    host,
    port: port > 0 ? port : DEFAULT_PORT,
    username,
    authType,
    privateKey: authType === 'privateKey' ? String(profile.privateKey || '').trim() : undefined,
    publicKey: authType === 'privateKey' ? String(profile.publicKey || '').trim() || undefined : undefined,
    privateKeyName: authType === 'privateKey' ? String(profile.privateKeyName || '').trim() || undefined : undefined,
    createdAt: String(profile.createdAt || now),
    updatedAt: String(profile.updatedAt || now),
  };
}

export function upsertSshProfile(profiles: SshProfile[], profile: SshProfile): SshProfile[] {
  const normalizedProfile = normalizeSshProfile(profile);
  if (!normalizedProfile) {
    return profiles;
  }

  const nextProfiles = profiles.filter((entry) => entry.id !== normalizedProfile.id);
  nextProfiles.push({
    ...normalizedProfile,
    updatedAt: new Date().toISOString(),
  });

  return normalizeSshProfiles(nextProfiles);
}

export function removeSshProfile(profiles: SshProfile[], profileId: string): SshProfile[] {
  return normalizeSshProfiles(profiles.filter((entry) => entry.id !== profileId));
}

export function findSshProfileByReference(profiles: SshProfile[], reference: string): SshProfile | null {
  const normalizedReference = reference.trim().toLowerCase();
  if (!normalizedReference) {
    return null;
  }

  return (
    profiles.find((profile) => profile.id.toLowerCase() === normalizedReference)
    || profiles.find((profile) => profile.name.toLowerCase() === normalizedReference)
    || null
  );
}

export function findMatchingSshProfile(
  profiles: SshProfile[],
  match: { host: string; username?: string | null; port?: number | null },
): SshProfile | null {
  const normalizedHost = match.host.trim().toLowerCase();
  const normalizedUsername = String(match.username || '').trim().toLowerCase();
  const requestedPort = typeof match.port === 'number' && Number.isFinite(match.port) ? match.port : null;

  if (!normalizedHost) {
    return null;
  }

  const hostMatches = profiles.filter((profile) => profile.host.trim().toLowerCase() === normalizedHost);
  if (!hostMatches.length) {
    return null;
  }

  const portMatches = requestedPort
    ? hostMatches.filter((profile) => profile.port === requestedPort)
    : hostMatches;

  if (normalizedUsername) {
    return portMatches.find((profile) => profile.username.trim().toLowerCase() === normalizedUsername)
      || hostMatches.find((profile) => profile.username.trim().toLowerCase() === normalizedUsername)
      || null;
  }

  if (portMatches.length === 1) {
    return portMatches[0];
  }

  if (hostMatches.length === 1) {
    return hostMatches[0];
  }

  return null;
}

export function createEmptySshProfile(): SshProfile {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    name: '',
    host: '',
    port: DEFAULT_PORT,
    username: '',
    authType: 'password',
    privateKey: '',
    publicKey: '',
    privateKeyName: '',
    createdAt: now,
    updatedAt: now,
  };
}